// ui/talentActivity.js
// Global talent activity tracking for sidebar display

import { skillRegistry } from '../core/skillRegistry.js';
import { getItemIcon, getItemName } from '../data/items/index.js';

// Proc tracking state
const talentActivity = {
    totalProcs: 0,
    recentProcs: [],      // Last 8 procs with details
    currentStreak: 0,
    lastProcTime: 0,
    streakDecayMs: 8000,  // Streak resets after 8 seconds of no procs
    maxRecentProcs: 8,
    currentSkillId: null
};

// Streak thresholds for visual feedback
const STREAK_THRESHOLDS = {
    HOT: 3,      // Hot streak!
    SURGE: 5,    // Surge!
    UNSTOPPABLE: 10  // UNSTOPPABLE!
};

/**
 * Record a talent proc for the activity panel
 * @param {string} skillId - The skill ID where the proc happened
 * @param {string} talentName - Display name of the talent
 * @param {string} reward - What the proc gave (e.g., "+1 Ruby", "2x Ore")
 * @param {string} icon - Optional icon for the talent (defaults to skill icon)
 * @param {Object} options - Optional additional data
 * @param {string} options.itemId - Item ID for showing icon in reward
 * @param {number} options.quantity - Quantity of item (default 1)
 */
export function recordTalentProc(skillId, talentName, reward, icon = null, options = {}) {
    const now = Date.now();

    // Check if streak should reset (too long since last proc)
    if (now - talentActivity.lastProcTime > talentActivity.streakDecayMs) {
        talentActivity.currentStreak = 0;
    }

    // Get skill info for icon fallback
    const skill = skillRegistry.get(skillId);
    const procIcon = icon || (skill?.icon) || '⚡';

    // Update tracking
    talentActivity.totalProcs++;
    talentActivity.currentStreak++;
    talentActivity.lastProcTime = now;
    talentActivity.currentSkillId = skillId;

    // Add to recent procs (keep last N)
    talentActivity.recentProcs.unshift({
        skillId: skillId,
        talent: talentName,
        reward: reward,
        icon: procIcon,
        itemId: options.itemId || null,
        quantity: options.quantity || null,
        time: now
    });

    if (talentActivity.recentProcs.length > talentActivity.maxRecentProcs) {
        talentActivity.recentProcs.pop();
    }

    // Update the UI panel
    updateTalentActivityPanel();
}

/**
 * Record a contract completion notification
 * @param {string} contractName - Name of the completed contract
 */
export function recordContractReady(contractName) {
    const now = Date.now();

    // Add to recent procs as a special "contract ready" entry
    talentActivity.recentProcs.unshift({
        skillId: 'bounty',
        talent: 'Contract Ready',
        reward: contractName,
        icon: '📜',
        itemId: null,
        quantity: null,
        isContract: true,
        time: now
    });

    if (talentActivity.recentProcs.length > talentActivity.maxRecentProcs) {
        talentActivity.recentProcs.pop();
    }

    // Update the UI panel
    updateTalentActivityPanel();
}

/**
 * Reset talent activity (called when switching skills or on init)
 */
export function resetTalentActivity() {
    talentActivity.totalProcs = 0;
    talentActivity.recentProcs = [];
    talentActivity.currentStreak = 0;
    talentActivity.lastProcTime = 0;
    talentActivity.currentSkillId = null;
    updateTalentActivityPanel();
}

/**
 * Get streak display class based on threshold
 */
function getStreakClass() {
    const streak = talentActivity.currentStreak;

    if (streak >= STREAK_THRESHOLDS.UNSTOPPABLE) {
        return 'streak-unstoppable';
    } else if (streak >= STREAK_THRESHOLDS.SURGE) {
        return 'streak-surge';
    } else if (streak >= STREAK_THRESHOLDS.HOT) {
        return 'streak-hot';
    }
    return '';
}

/**
 * Get remaining time until streak resets
 */
function getStreakTimeRemaining() {
    if (talentActivity.currentStreak === 0 || talentActivity.lastProcTime === 0) {
        return 0;
    }
    const elapsed = Date.now() - talentActivity.lastProcTime;
    const remaining = talentActivity.streakDecayMs - elapsed;
    return Math.max(0, remaining);
}

/**
 * Render the talent activity panel content
 */
function renderTalentActivityContent() {
    const { totalProcs, recentProcs, currentStreak } = talentActivity;
    const streakClass = getStreakClass();
    const timeRemaining = getStreakTimeRemaining();
    const timeRemainingSeconds = Math.ceil(timeRemaining / 1000);

    // Empty state
    if (totalProcs === 0) {
        return `
            <div class="talent-activity-empty">
                <div class="empty-icon">⚡</div>
                <div class="empty-text">No talent procs yet</div>
                <div class="empty-hint">Start a skill to see your talents in action!</div>
            </div>
        `;
    }

    // Build recent procs list
    const procsHtml = recentProcs.map((proc, index) => {
        const isNew = index === 0 && (Date.now() - proc.time < 500);

        // Build reward display - use icon if itemId available
        let rewardHtml;
        if (proc.itemId) {
            const itemIcon = getItemIcon(proc.itemId);
            const qty = proc.quantity || 1;
            const prefix = qty > 1 ? `${qty}x` : '+1';
            rewardHtml = `<span class="proc-reward proc-reward-icon">${prefix} <span class="proc-item-icon">${itemIcon}</span></span>`;
        } else if (proc.reward) {
            rewardHtml = `<span class="proc-reward">${proc.reward}</span>`;
        } else {
            rewardHtml = '';
        }

        const entryClass = proc.isContract ? 'talent-proc-entry contract-ready' : 'talent-proc-entry';

        // Build tooltip text
        let titleText;
        if (proc.itemId) {
            const qty = proc.quantity || 1;
            const name = getItemName(proc.itemId);
            titleText = qty > 1 ? `${qty}x ${name}` : name;
        } else if (proc.isContract) {
            titleText = `Contract Ready: ${proc.reward}`;
        } else if (proc.reward) {
            titleText = proc.reward;
        } else {
            titleText = proc.talent;
        }

        return `
            <div class="${entryClass} ${isNew ? 'new' : ''}" title="${titleText}">
                <span class="proc-talent">${proc.talent}</span>
                <span class="proc-arrow">→</span>
                ${rewardHtml}
            </div>
        `;
    }).join('');

    return `
        <div class="talent-activity-content ${streakClass}">
            ${currentStreak >= 2 ? `
                <div class="talent-streak-banner">
                    <span class="streak-label">Streak:</span>
                    <span class="streak-count">${currentStreak}x</span>
                    <span class="streak-timer">${timeRemainingSeconds}s</span>
                </div>
            ` : ''}
            <div class="talent-proc-log">
                ${procsHtml}
            </div>
            <div class="talent-activity-footer">
                <span class="talent-proc-total">${totalProcs} proc${totalProcs !== 1 ? 's' : ''} this session</span>
            </div>
        </div>
    `;
}

/**
 * Update the talent activity panel in the sidebar
 */
export function updateTalentActivityPanel() {
    const container = document.getElementById('sidebar-talent-activity');
    if (container) {
        container.innerHTML = renderTalentActivityContent();
    }
}

/**
 * Check and decay streak if needed (call this periodically)
 */
export function checkStreakDecay() {
    if (talentActivity.currentStreak > 0) {
        const timeSinceLastProc = Date.now() - talentActivity.lastProcTime;
        if (timeSinceLastProc > talentActivity.streakDecayMs) {
            talentActivity.currentStreak = 0;
        }
        // Always update to refresh the countdown timer
        updateTalentActivityPanel();
    }
}

/**
 * Initialize the talent activity system
 */
export function initTalentActivity() {
    // Initial render
    updateTalentActivityPanel();

    // Check for streak decay every second
    setInterval(checkStreakDecay, 1000);
}

// Export for window access
if (typeof window !== 'undefined') {
    window.talentActivity = {
        record: recordTalentProc,
        recordContract: recordContractReady,
        reset: resetTalentActivity,
        update: updateTalentActivityPanel
    };
}

export default {
    recordTalentProc,
    recordContractReady,
    resetTalentActivity,
    updateTalentActivityPanel,
    checkStreakDecay,
    initTalentActivity
};
